import Http from '../../../../../core/Http';
import i18n from '../../../../../assets/utils/comm/i18n';
import { proposalTargetDisplayName } from '../../projects/comm/proposalTargetDisplayName';
import { ALLIANCE_INVITE_NAME_MISSING_LABEL_KO } from '../domain/allianceUiCodes';
import { getDefaultAttachedFormConfig } from './AttachedFormsSection';

/** 서버 Page API를 size 단위로 순회해 list 합침 */
const ALLIANCE_SOURCE_PAGE_SIZE = 10;
const ALLIANCE_SOURCE_MAX_PAGES = 500;

function listChunkFromPage(res) {
  if (!res) return [];
  if (Array.isArray(res.list)) return res.list;
  if (Array.isArray(res.content)) return res.content;
  return [];
}

async function fetchAllListPagesMerged(url, baseParams = {}) {
  const out = [];
  let pageNumber = 1;
  let totalPages = 1;
  while (pageNumber <= totalPages && pageNumber <= ALLIANCE_SOURCE_MAX_PAGES) {
    const res = await Http.get(url, {
      params: { ...baseParams, pageNumber, size: ALLIANCE_SOURCE_PAGE_SIZE },
    }).catch(() => null);
    const chunk = listChunkFromPage(res);
    out.push(...chunk);
    const tp = Number(res?.totalPages);
    if (Number.isFinite(tp) && tp >= 1) {
      totalPages = tp;
    } else {
      const te = Number(res?.totalElements);
      if (Number.isFinite(te) && ALLIANCE_SOURCE_PAGE_SIZE > 0) {
        totalPages = Math.max(1, Math.ceil(te / ALLIANCE_SOURCE_PAGE_SIZE));
      } else if (chunk.length < ALLIANCE_SOURCE_PAGE_SIZE) {
        break;
      } else {
        totalPages = pageNumber + 1;
      }
    }
    pageNumber += 1;
  }
  return out;
}

const MOCK_TASKS_TECH_DEMAND = [
  { id: 'td1', taskId: 'td1', linkedTaskNo: 'T-2024-001', title: 'AI 기반 품질 검사 기술 개발' },
  { id: 'td2', taskId: 'td2', linkedTaskNo: 'T-2024-002', title: '스마트팩토리 자동화 인프라 구축' },
  { id: 'td3', taskId: 'td3', linkedTaskNo: 'T-2024-003', title: '에너지 효율화 IoT 플랫폼' },
];

const MOCK_TASKS_RESEARCH = [
  { id: 'rs1', taskId: 'rs1', linkedTaskNo: 'R-2024-001', title: '연구개발 기반 기술 이전 사업' },
  { id: 'rs2', taskId: 'rs2', linkedTaskNo: 'R-2024-002', title: '산학협력 고도화 지원 사업' },
  { id: 'rs3', taskId: 'rs3', linkedTaskNo: 'R-2024-003', title: '지역혁신 R&D 사업' },
];

const toSpaceTypeCd = (spaceType) => {
  // DB ck_gas_space_type: 보통 GENERAL 미허용 → 미선택도 TECH_DEMAND 로 저장(연결 없음은 source_uid=0)
  if (!spaceType || spaceType === 'unspecified') return 'TECH_DEMAND';
  if (spaceType === 'research-project') return 'RESEARCH_PROJECT';
  return 'TECH_DEMAND';
};

const fromSpaceTypeCd = (spaceTypeCd, sourceUid) => {
  if (spaceTypeCd === 'RESEARCH_PROJECT') return 'research-project';
  if (spaceTypeCd === 'GENERAL') return 'unspecified';
  const uid = sourceUid != null ? Number(sourceUid) : NaN;
  if (spaceTypeCd === 'TECH_DEMAND' && (!Number.isFinite(uid) || uid <= 0)) return 'unspecified';
  if (spaceTypeCd === 'COMMUNITY_PROJECT' && Number.isFinite(uid) && uid > 0) return 'community-project';
  return 'tech-demand';
};

const toSourceTypeCd = (sourceType, spaceType) => {
  if (sourceType === 'research' || sourceType === 'RND_ANNOUNCEMENT') return 'RND_ANNOUNCEMENT';
  if (sourceType === 'tech-demand' || sourceType === 'TECH_DEMAND') return 'TECH_DEMAND';
  if (sourceType === 'community-project' || sourceType === 'COMMUNITY_PROJECT') return 'COMMUNITY_PROJECT';
  if (spaceType === 'research-project') return 'RND_ANNOUNCEMENT';
  return 'TECH_DEMAND';
};

const resolveHasLinkedSource = (payload) => {
  const rawUid = Number(payload?.sourceId ?? payload?.taskId);
  if (!Number.isFinite(rawUid) || rawUid <= 0) return false;
  const st = payload?.sourceType;
  if (
    st === 'research' ||
    st === 'tech-demand' ||
    st === 'RND_ANNOUNCEMENT' ||
    st === 'TECH_DEMAND' ||
    st === 'community-project' ||
    st === 'COMMUNITY_PROJECT'
  ) {
    return true;
  }
  return (
    payload?.spaceType === 'research-project' ||
    payload?.spaceType === 'tech-demand' ||
    payload?.spaceType === 'community-project'
  );
};

const safeArray = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.list)) return res.list;
  return [];
};

const normalizeMemberName = (row) => {
  const fromProposal = proposalTargetDisplayName(row);
  if (fromProposal && fromProposal !== '(이름없음)') return fromProposal;
  return row?.name || row?.userName || '-';
};

const normalizeApplicantName = (row) => {
  let form = row?.applicationFormData;
  if (typeof form === 'string') {
    try {
      form = JSON.parse(form);
    } catch (e) {
      form = null;
    }
  }
  return form?.org_name || form?.orgName || row?.regNm || row?.name || '-';
};

const normalizeAttachmentMeta = (att) => {
  if (!att) return null;
  return {
    docId: att.docId || '',
    documentType: att.documentType || '',
    fileId: att.fileId ? String(att.fileId) : '',
    fileUid: att.fileUid != null ? Number(att.fileUid) : null,
    orgFileNm: att.orgFileNm || '',
    fileType: att.fileType || '',
    extension: att.extension || '',
    saved: att.saved || 'N',
  };
};

const mapFileAddFromAttachments = (attachments) => {
  return (attachments || [])
    .map((att) => normalizeAttachmentMeta(att))
    .filter((att) => att && att.fileId)
    .map((att) => ({
      fileId: att.fileId,
      orgFileNm: att.orgFileNm || att.fileId,
      fileType: att.fileType || '',
      extension: att.extension || '',
      saved: att.saved || 'N',
    }));
};

const mapAttachedFormData = (attachments) => {
  const mapped = (attachments || [])
    .map((att) => normalizeAttachmentMeta(att))
    .filter(Boolean);
  return JSON.stringify(mapped);
};

const normalizeThumbnailMeta = (thumb) => {
  if (!thumb || !thumb.fileId) return null;
  return {
    fileId: String(thumb.fileId),
    orgFileNm: thumb.orgFileNm || thumb.fileId,
    fileType: thumb.fileType || '',
    extension: thumb.extension || '',
    saved: thumb.saved || 'N',
  };
};

export const ALLIANCE_CREATE_SOURCE_TYPE_LABEL_KO = {
  'tech-demand': '기술수요 공고',
  'community-project': '오픈 커뮤니티 프로젝트 공고',
  research: '연구과제',
};

export function mapAllianceCreateContextFromDemand({ sourceType, sourceId, sourceTitle }) {
  const id = sourceId != null ? String(sourceId).trim() : '';
  if (!id) return null;
  const type = sourceType === 'community-project' ? 'community-project' : 'tech-demand';
  return {
    sourceType: type,
    sourceId: id,
    sourceTitle: sourceTitle != null ? String(sourceTitle).trim() : '',
    sourceTypeLabel: ALLIANCE_CREATE_SOURCE_TYPE_LABEL_KO[type] || '공고',
    spaceType: type,
  };
}

export async function getAllianceCandidateMembers(sourceType, sourceUid) {
  if (!sourceUid) {
    return { accepted: [], waiting: [], rejected: [] };
  }

  if (sourceType === 'community-project' || sourceType === 'COMMUNITY_PROJECT') {
    const [proposalRows, applicationRows] = await Promise.all([
      fetchAllListPagesMerged('/api/community-projects/proposals', { demandId: sourceUid }).catch(() => []),
      fetchAllListPagesMerged('/api/community-projects/applications', { demandId: sourceUid }).catch(() => []),
    ]);
    const accepted = [];
    const waiting = [];
    const rejected = [];
    const seenAccepted = new Set();
    const seenWaiting = new Set();
    const seenRejected = new Set();

    proposalRows.forEach((row) => {
      const item = {
        userUid: Number(row.targetUserId),
        name: normalizeMemberName(row),
        status: row.acceptStatus === true ? 'ACCEPTED' : row.acceptStatus === false ? 'REJECTED' : 'WAITING',
      };
      const key = `${item.userUid}:${item.name}`;
      if (item.status === 'ACCEPTED') {
        if (!seenAccepted.has(key)) {
          seenAccepted.add(key);
          accepted.push(item);
        }
      } else if (item.status === 'REJECTED') {
        if (!seenRejected.has(key)) {
          seenRejected.add(key);
          rejected.push(item);
        }
      } else if (!seenWaiting.has(key)) {
        seenWaiting.add(key);
        waiting.push(item);
      }
    });

    applicationRows.forEach((row) => {
      const item = {
        userUid: Number(row.regUid),
        name: normalizeApplicantName(row),
        status: String(row.resultStatusCd || '').toUpperCase() === 'ACCEPTED'
          ? 'ACCEPTED'
          : String(row.resultStatusCd || '').toUpperCase() === 'REJECTED'
            ? 'REJECTED'
            : 'WAITING',
      };
      const key = `${item.userUid}:${item.name}`;
      if (item.status === 'ACCEPTED') {
        if (!seenAccepted.has(key)) {
          seenAccepted.add(key);
          accepted.push(item);
        }
      } else if (item.status === 'REJECTED') {
        if (!seenRejected.has(key)) {
          seenRejected.add(key);
          rejected.push(item);
        }
      } else if (!seenWaiting.has(key)) {
        seenWaiting.add(key);
        waiting.push(item);
      }
    });

    return { accepted, waiting, rejected };
  }

  if (sourceType === 'research') {
    const rows = await fetchAllListPagesMerged('/api/research/collaboration-proposals/my-sent', {
      rndAnnouncementUid: sourceUid,
    }).catch(() => []);
    const accepted = [];
    const waiting = [];
    const rejected = [];

    rows.forEach((row) => {
      const item = {
        userUid: Number(row.receiverUid),
        name: normalizeMemberName(row),
        status: String(row.proposalStatus || '').toUpperCase(),
      };
      if (item.status === 'ACCEPTED') accepted.push(item);
      else if (item.status === 'REJECTED') rejected.push(item);
      else waiting.push(item);
    });

    return { accepted, waiting, rejected };
  }

  const [proposalRows, applicationRows] = await Promise.all([
    fetchAllListPagesMerged('/api/tech-demands/proposals', { demandId: sourceUid }).catch(() => []),
    fetchAllListPagesMerged('/api/tech-demands/applications', { demandId: sourceUid }).catch(() => []),
  ]);
  const accepted = [];
  const waiting = [];
  const rejected = [];
  const seenAccepted = new Set();
  const seenWaiting = new Set();
  const seenRejected = new Set();

  proposalRows.forEach((row) => {
    const item = {
      userUid: Number(row.targetUserId),
      name: normalizeMemberName(row),
      status: row.acceptStatus === true ? 'ACCEPTED' : row.acceptStatus === false ? 'REJECTED' : 'WAITING',
    };
    const key = `${item.userUid}:${item.name}`;
    if (item.status === 'ACCEPTED') {
      if (!seenAccepted.has(key)) {
        seenAccepted.add(key);
        accepted.push(item);
      }
    } else if (item.status === 'REJECTED') {
      if (!seenRejected.has(key)) {
        seenRejected.add(key);
        rejected.push(item);
      }
    } else {
      if (!seenWaiting.has(key)) {
        seenWaiting.add(key);
        waiting.push(item);
      }
    }
  });

  applicationRows.forEach((row) => {
    const item = {
      userUid: Number(row.regUid),
      name: normalizeApplicantName(row),
      status: String(row.resultStatusCd || '').toUpperCase() === 'ACCEPTED'
        ? 'ACCEPTED'
        : String(row.resultStatusCd || '').toUpperCase() === 'REJECTED'
          ? 'REJECTED'
          : 'WAITING',
    };
    const key = `${item.userUid}:${item.name}`;
    if (item.status === 'ACCEPTED') {
      if (!seenAccepted.has(key)) {
        seenAccepted.add(key);
        accepted.push(item);
      }
    } else if (item.status === 'REJECTED') {
      if (!seenRejected.has(key)) {
        seenRejected.add(key);
        rejected.push(item);
      }
    } else {
      if (!seenWaiting.has(key)) {
        seenWaiting.add(key);
        waiting.push(item);
      }
    }
  });

  return { accepted, waiting, rejected };
}

const ALLIANCE_INVITE_TARGET_PAGE_SIZE = 500;

/**
 * 협력 제안 대상 API와 동일 기준의 활성 회원(미차단·미탈퇴) 목록.
 * demandId/rndAnnouncementUid 없이 호출하면 공고·과제와 무관한 전체 후보가 나온다.
 */
export async function fetchAllianceInviteablePlatformUsers(memberTypeForApi, excludeLoginId) {
  let basePath;
  if (memberTypeForApi === 'COMMUNITY_PROJECT') {
    basePath = '/api/community-projects/proposals/targets';
  } else if (memberTypeForApi === 'research') {
    basePath = '/api/research/collaboration-proposals/targets';
  } else {
    basePath = '/api/tech-demands/proposals/targets';
  }
  const excludeQ =
    excludeLoginId && memberTypeForApi !== 'research'
      ? `&excludeUserId=${encodeURIComponent(excludeLoginId)}`
      : '';
  const all = [];
  for (let pageNumber = 1; pageNumber <= 40; pageNumber += 1) {
    const url = `${basePath}?size=${ALLIANCE_INVITE_TARGET_PAGE_SIZE}&pageNumber=${pageNumber}${excludeQ}`;
    const res = await Http.get(url).catch(() => null);
    const chunk = safeArray(res);
    all.push(...chunk);
    if (chunk.length < ALLIANCE_INVITE_TARGET_PAGE_SIZE) break;
  }
  return all;
}

/** 플랫폼 회원 목록 + 연결 과제 지원·제안 참여자를 userUid 기준으로 합친다. */
/** GET /api/alli/spaces/{spaceUid}/invite-candidates 응답의 UserList 행 → 초대 팝업 후보 객체 */
export function mapAllianceInviteCandidateFromUserList(row) {
  const uid = row?.uid ?? row?.UID;
  if (uid == null || Number.isNaN(Number(uid))) return null;
  const workplace = row?.workplaceNm && String(row.workplaceNm).trim();
  const dept = row?.deptNm && String(row.deptNm).trim();
  const orgHint = workplace || dept || '';
  const userRoleCd =
    row?.userRoleCd != null
      ? String(row.userRoleCd)
      : row?.user_role_cd != null
        ? String(row.user_role_cd)
        : '';
  const nm = row?.userNm && String(row.userNm).trim();
  return {
    userUid: String(uid),
    label: nm || '(이름 없음)',
    userId: row?.userId != null ? String(row.userId) : '',
    roleCd: row?.roleCd != null ? String(row.roleCd) : '',
    orgHint,
    userRoleCd,
  };
}

export function mergeAllianceInviteCandidates(platformUserRows, taskMemberRows) {
  const byUid = new Map();
  (platformUserRows || []).forEach((row) => {
    const uid = row?.uid;
    if (uid == null || Number.isNaN(Number(uid))) return;
    const key = String(uid);
    const nm = row?.userNm && String(row.userNm).trim();
    const workplace = row?.workplaceNm && String(row.workplaceNm).trim();
    const dept = row?.deptNm && String(row.deptNm).trim();
    const orgHint = workplace || dept || '';
    const userRoleCd =
      row?.userRoleCd != null
        ? String(row.userRoleCd)
        : row?.user_role_cd != null
          ? String(row.user_role_cd)
          : '';
    byUid.set(key, {
      userUid: key,
      label: nm || ALLIANCE_INVITE_NAME_MISSING_LABEL_KO,
      userId: row?.userId != null ? String(row.userId) : '',
      roleCd: row?.roleCd != null ? String(row.roleCd) : '',
      orgHint,
      userRoleCd,
    });
  });
  (taskMemberRows || []).forEach((row) => {
    const uid = row?.userUid;
    if (uid == null || Number.isNaN(Number(uid))) return;
    const key = String(uid);
    const label = row?.name && String(row.name).trim();
    const existing = byUid.get(key);
    if (existing) {
      if (
        label &&
        (existing.label === ALLIANCE_INVITE_NAME_MISSING_LABEL_KO || label.length > existing.label.length)
      ) {
        existing.label = label;
      }
    } else {
      byUid.set(key, {
        userUid: key,
        label: label || ALLIANCE_INVITE_NAME_MISSING_LABEL_KO,
        userId: '',
        roleCd: '',
        orgHint: '',
        userRoleCd: '',
      });
    }
  });
  const out = Array.from(byUid.values());
  out.sort((a, b) => String(a.label).localeCompare(String(b.label), 'ko'));
  return out;
}

export async function getTaskListByType(spaceType) {
  if (!spaceType || spaceType === 'unspecified') {
    return [];
  }
  if (spaceType === 'tech-demand') {
    const rows = await fetchAllListPagesMerged('/api/tech-demands/my', {}).catch(() => []);
    const list = rows.map((dto) => ({
      id: String(dto.uid),
      taskId: String(dto.uid),
      linkedTaskNo: String(dto.uid),
      title: dto.title || `기술수요 #${dto.uid}`,
    }));
    if (list.length > 0) return list;
  }

  return spaceType === 'research-project' ? [...MOCK_TASKS_RESEARCH] : [...MOCK_TASKS_TECH_DEMAND];
}

export function getAttachedFormConfig() {
  const t = (key, opts) => (i18n?.isInitialized ? i18n.t(key, opts) : key);
  return Promise.resolve(getDefaultAttachedFormConfig(t));
}

export async function getSpaceById(id) {
  if (!id) return null;
  const res = await Http.get(`/api/alli/spaces/${id}`).catch(() => null);
  if (!res) return null;

  const attachedForms = (() => {
    try {
      const parsed = typeof res.attachedFormData === 'string' ? JSON.parse(res.attachedFormData || '[]') : res.attachedFormData;
      return Array.isArray(parsed)
        ? parsed.map((item) => ({
            ...item,
            downloadUrl: item?.fileId ? `/api/glow/files/download?fileId=${encodeURIComponent(item.fileId)}` : '',
          }))
        : [];
    } catch (e) {
      return [];
    }
  })();

  const allianceObjectData = (() => {
    try {
      if (!res.allianceObjectData) return {};
      if (typeof res.allianceObjectData === 'string') {
        const parsed = JSON.parse(res.allianceObjectData);
        return parsed && typeof parsed === 'object' ? parsed : {};
      }
      return typeof res.allianceObjectData === 'object' ? res.allianceObjectData : {};
    } catch (e) {
      return {};
    }
  })();

  return {
    id: String(res.uid),
    name: res.spaceNm || '',
    status: String(res.approvalStatusCd || '').toLowerCase() || 'pending',
    requestedAt: res.regDt || '',
    linkedTaskName: res.sourceUid > 0 ? `#${res.sourceUid}` : '',
    description: res.spaceDesc || '',
    purpose: res.spaceDesc || '',
    spaceType: fromSpaceTypeCd(res.spaceTypeCd, res.sourceUid),
    taskId: res.sourceUid > 0 ? String(res.sourceUid) : '',
    linkedTaskNo: res.sourceUid > 0 ? String(res.sourceUid) : '',
    rejectedReason: res.rejectReason || '',
    ownerUid: res.ownerUid ?? null,
    ownerNm: res.ownerNm || '',
    sourceDetailUrl: res.sourceDetailUrl || null,
    approvedUid: res.approvedUid ?? null,
    approvedNm: res.approvedNm || '',
    approvedAt: res.approvedAt || '',
    rejectedUid: res.rejectedUid ?? null,
    rejectedNm: res.rejectedNm || '',
    rejectedAt: res.rejectedAt || '',
    stageCd: res.stageCd || '',
    adminClosedYn: String(res.adminClosedYn || 'N').toUpperCase() === 'Y' ? 'Y' : 'N',
    sourceType: res.sourceTypeCd || '',
    sourceId: res.sourceUid || null,
    sourceName: res.sourceNm || '',
    fileUid: res.fileUid || null,
    thumbnailFileUid: res.thumbnailFileUid || null,
    thumbnailPreview: res.thumbnailFileUid ? `/api/glow/files/viewByUid/${res.thumbnailFileUid}` : '',
    attachedFormData: res.attachedFormData || '[]',
    attachedForms,
    allianceObjectData,
  };
}

export const SPACE_CREATE_API_ERROR_KEYS = {
  nameRequired: 'alli.create.validationNameRequired',
  createFailed: 'alli.create.createFailed',
  spaceIdRequired: 'alli.create.apiSpaceIdRequired',
};

export async function createSpace(payload) {
  if (!payload?.name?.trim()) throw new Error(SPACE_CREATE_API_ERROR_KEYS.nameRequired);

  const rawUid = Number(payload?.sourceId ?? payload?.taskId);
  const hasLinkedSource = resolveHasLinkedSource(payload);

  const attachments = Array.isArray(payload?.attachments) ? payload.attachments : [];
  const thumbnail = normalizeThumbnailMeta(payload?.thumbnailAttachment);
  const hasExplicitInviteList = Array.isArray(payload.initialInviteUserUids);
  const inviteUids = hasExplicitInviteList
    ? payload.initialInviteUserUids
        .map((uid) => Number(uid))
        .filter((uid) => Number.isFinite(uid) && uid > 0)
    : [];

  const request = {
    spaceNm: payload.name.trim(),
    spaceDesc: payload.description || payload.purpose || '',
    spaceTypeCd: toSpaceTypeCd(payload.spaceType),
    sourceTypeCd: hasLinkedSource ? toSourceTypeCd(payload.sourceType, payload.spaceType) : null,
    sourceUid: hasLinkedSource ? rawUid : null,
    stageCd: 'PRE',
    allianceObjectData: payload.allianceObjectData || {},
    // 승인 상태는 서버(glow.alliance-space.require-admin-approval)에서만 결정
    thumbnailFileAdd: thumbnail ? [thumbnail] : [],
    attachedFormData: mapAttachedFormData(attachments),
    fileAdd: mapFileAddFromAttachments(attachments),
    ...(hasLinkedSource && hasExplicitInviteList ? { initialInviteUserUids: inviteUids } : {}),
  };

  const res = await Http.post('/api/alli/spaces', request);
  const spaceUid = Number(res?.uid ?? res);
  if (!Number.isFinite(spaceUid) || spaceUid <= 0) {
    throw new Error(SPACE_CREATE_API_ERROR_KEYS.createFailed);
  }

  const idStr = String(spaceUid);
  const space = await getSpaceById(idStr);
  const status = space?.status || 'pending';

  return { id: idStr, status };
}

export async function getSpaceForEdit(id) {
  const s = await getSpaceById(id);
  if (!s) return null;
  const description = (s.description || s.purpose || '').trim();
  return {
    id: s.id,
    name: s.name,
    description,
    spaceType: s.spaceType,
    purpose: s.purpose || '',
    taskId: s.taskId,
    linkedTaskNo: s.linkedTaskNo,
    fileUid: s.fileUid || null,
    thumbnailFileUid: s.thumbnailFileUid || null,
    thumbnailPreview: s.thumbnailPreview || '',
    sourceType: s.sourceType || '',
    sourceId: s.sourceId || null,
    attachedFormData: s.attachedFormData || '[]',
    attachedForms: Array.isArray(s.attachedForms) ? s.attachedForms.map((item) => normalizeAttachmentMeta(item)).filter(Boolean) : [],
  };
}

export async function updateSpace(id, payload) {
  if (!id) throw new Error(SPACE_CREATE_API_ERROR_KEYS.spaceIdRequired);
  if (!payload?.name?.trim()) throw new Error(SPACE_CREATE_API_ERROR_KEYS.nameRequired);

  const rawUid = Number(payload?.sourceId ?? payload?.taskId);
  const hasLinkedSource = resolveHasLinkedSource(payload);
  const attachments = Array.isArray(payload?.attachments) ? payload.attachments : [];
  const thumbnail = normalizeThumbnailMeta(payload?.thumbnailAttachment);
  const request = {
    spaceNm: payload.name.trim(),
    spaceDesc: payload.description || payload.purpose || '',
    spaceTypeCd: toSpaceTypeCd(payload.spaceType),
    sourceTypeCd: hasLinkedSource ? toSourceTypeCd(payload.sourceType, payload.spaceType) : null,
    sourceUid: hasLinkedSource ? rawUid : null,
    fileUid: payload.fileUid != null ? Number(payload.fileUid) : null,
    thumbnailFileUid: payload.thumbnailFileUid != null ? Number(payload.thumbnailFileUid) : null,
    thumbnailFileAdd: thumbnail ? [thumbnail] : [],
    thumbnailFileDel: Array.isArray(payload.thumbnailFileDel) ? payload.thumbnailFileDel : [],
    attachedFormData: mapAttachedFormData(attachments),
    fileAdd: mapFileAddFromAttachments(attachments),
    fileDel: Array.isArray(payload.fileDel) ? payload.fileDel : [],
  };

  await Http.put(`/api/alli/spaces/${id}`, request);
  const idStr = String(id);
  const space = await getSpaceById(idStr);
  const status = space?.status || 'pending';
  return { id: idStr, status };
}
